import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { useBackendApi } from '../../../helpers/backendHelper';
import { useTakesState } from '../../../helpers/takesHelper';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import { santizeTwitterHTML, trackClairtyEvent, tweeterTimestamp } from '../../../utils';
import { Flexbox, FlexboxColumn } from '../../action/Flexbox';
import { Line } from '../../action/Line';
import RedirectButton from '../../action/RedirectButton';
import Logo from '../../Logo/Logo';
import ProjectTag from '../../ProjectTag/ProjectTag';
import ThemedText from '../../theme/ThemedText';
import ThemeLogo from '../../theme/ThemeLogo';
import ImageAttachments from '../ImageAttachments/ImageAttachments';
import ProjectChip from '../Projectchip/ProjectChip';
import CommentWithReadMore from './Comment';
import Reactions from './Reactions';
import './ReviewCard.scss';
import ReviewCardSkeleton from './ReviewCardSkelton';
import Voting from './Voting';

const TwitterReviewDetailCard = forwardRef(({ maxWidth = false, showNoOfReviews = true, data, isDetailed = true }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedState = location.state;
  const { uuid } = useParams();
  const [{ user }] = useGlobalState();

  const [{ reactionEligible }] = useTakesState();
  const postCommentRef = useRef(null);
  const [openCommentSection, setOpenCommentSection] = useState(false);
  const backendHelper = useBackendApi();
  const [postData, setPostData] = useImmer();
  const [showVoting, setShowVoting] = useState(true);
  const [showProtocolStats, setShowProtocolStats] = useState(false);

  const handleOpenCommentSection = () => {
    setOpenCommentSection(!openCommentSection);
    postCommentRef?.current?.expand();
  };

  async function deleteComment(commentId) {
    if (!commentId) return;

    try {
      await backendHelper.reviews.deleteComment(commentId);
      setPostData(state => {
        state.comments = [];
      });
      alert.success('Comment deleted');
    } catch (error) {
      alert.error('Something went wrong!');
    }
  }

  async function getPostData() {
    try {
      const body = {
        filter: {
          uuid,
          tweetsOnly: true,
          pagination: {
            page: 1,
            limit: 5,
          },
        },
      };
      const response = await backendHelper.reviews.getFeed(body);
      setPostData(response?.data?.[0]);
    } catch (error) {}
  }

  function handleRedirect(address) {
    const url = `${window.location.origin}/profile/${address}/projects`;
    window.open(url, '_blank');
  }

  function handleTwitterRedirect(event, username) {
    event?.stopPropagation();
    window.open(`https://x.com/${username}`, '_target');
  }

  function handleBack() {
    navigate(-1);
  }

  useEffect(() => {
    if (data) return setPostData(JSON.parse(JSON.stringify(data)));
    if (postData) return;
    if (passedState) {
      return setPostData(passedState);
    }
    getPostData();
  }, [passedState, uuid, data]);

  useEffect(() => {
    if (!postData) return;
    if (user === undefined) return;
    trackClairtyEvent('detailed_take', {
      post_id: postData?.uuid,
      project: postData?.protocol,
      reaction_user_state: postData?.userReaction,
      user_state: user ? 'logged_in' : 'logged_out',
      take_type: 'x_take',
    });
  }, [postData, user]);

  if (!postData)
    return (
      <FlexboxColumn fw alignItems={'flex-start'} className='feed_wrapper'>
        <ReviewCardSkeleton />
      </FlexboxColumn>
    );

  return (
    <FlexboxColumn fw alignItems={'flex-start'} className='feed_wrapper' justifyContent='flex-start'>
      {passedState && (
        <Flexbox className='pointer' onClick={handleBack} gap='0.5rem'>
          <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path d='M12.6668 8H3.3335' stroke='#888888' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' />
            <path d='M8.00016 12.6668L3.3335 8.00016L8.00016 3.3335' stroke='#888888' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round' />
          </svg>
          <ThemedText.P color='color-xlight' weight='semi-bold'>
            Back
          </ThemedText.P>
        </Flexbox>
      )}

      <FlexboxColumn className={`reviewcard_wrapper ${maxWidth && 'review_wrapper_width'}`} gap='1.5rem'>
        <Flexbox justifyContent='space-between' fw className='reviewcard_wrapper_heading'>
          <Flexbox gap='0.5rem' className='reviewcard_wrapper_user'>
            {postData?.tweetData?.tweeter_username ? (
              <>
                <Logo src={postData?.tweetData?.tweeter_pfp} width={'1.45rem'} height={'1.45rem'} borderRadius='50%' />
                <ThemedText.H6 weight='medium' onClick={event => handleTwitterRedirect(event, `@${postData?.tweetData?.tweeter_username}`)}>
                  @{postData?.tweetData?.tweeter_username}
                </ThemedText.H6>{' '}
                <ThemedText.H6 className='reviewcard_wrapper_light'>tweeted about</ThemedText.H6> <ProjectChip project={postData?.protocol} />{' '}
                <ThemedText.H6 className='reviewcard_wrapper_light'>{tweeterTimestamp({ timestamp: parseInt(postData?.tweetTimestamp) })}</ThemedText.H6>
              </>
            ) : (
              <>
                {' '}
                <ProjectChip project={postData?.protocol} />
                <ThemedText.H6 className='reviewcard_wrapper_light'>tweeted {tweeterTimestamp({ timestamp: parseInt(postData?.tweetTimestamp) })}</ThemedText.H6>
              </>
            )}
          </Flexbox>
          <RedirectButton url={postData?.tweetUrl}>
            <Flexbox gap='0.5rem'>
              <ThemeLogo icon={'twitterLogo'} />
              <ThemedText.H6>Visit Tweet</ThemedText.H6>
            </Flexbox>
          </RedirectButton>
        </Flexbox>

        <div className='reviewcard_wrapper_review'>
          <div
            dangerouslySetInnerHTML={{
              __html: santizeTwitterHTML(postData?.content),
            }}
          />
        </div>
        <Line />
        <Reactions postData={postData} handlePostData={setPostData} twitterTake={true} isDetailed={isDetailed || data} />
        {postData?.comments?.length > 0 && (
          <>
            <FlexboxColumn fw gap='0.75rem'>
              <Flexbox justifyContent='space-between' fw>
                <Flexbox gap='0.5rem' className='reviewcard_wrapper_user' onClick={() => handleRedirect(postData?.comments?.[0]?.user?.address)}>
                  <Logo src={postData?.comments?.[0]?.user?.avatarUrl} width={'1.5rem'} height={'1.5rem'} borderRadius='50%' />
                  <ThemedText.H6 weight='medium'>{postData?.comments?.[0]?.user?.name}</ThemedText.H6>
                  <ProjectTag project={postData?.protocol} />
                  <ThemedText.H6 className='reviewcard_wrapper_light'>replied</ThemedText.H6>
                </Flexbox>
                {user?.address === postData?.comments?.[0]?.user?.address && postData?.comments?.[0]?.commentid && (
                  <ThemedText.H6 onClick={() => deleteComment(postData?.comments?.[0]?.commentid)} className='reviewcard_wrapper_light pointer'>
                    delete reply
                  </ThemedText.H6>
                )}
              </Flexbox>
              <div className='reviewcard_wrapper_review'>
                <CommentWithReadMore content={postData?.comments?.[0]?.content} />
                <ImageAttachments attachments={postData?.attachments} />
              </div>
            </FlexboxColumn>
            <Line />
          </>
        )}

        {/* <Flexbox onClick={() => setShowVoting(!showVoting)} className='pointer'>
          <ThemedText.H6>Who's Voting</ThemedText.H6>
          {showVoting ? <ThemeLogo icon={'blueArrowUp'} /> : <ThemeLogo icon={'blueArrowDown'} />}
        </Flexbox> */}
        {showVoting && <Voting postData={postData} />}
        <Line />
        {postData?.tweetData?.grok_summary && (
          <div className='reviewcard_wrapper_review grok_summary'>
            <ThemedText.H5 weight='medium'>Grok Summary</ThemedText.H5>
            <div
              dangerouslySetInnerHTML={{
                __html: santizeTwitterHTML(postData?.tweetData?.grok_summary),
              }}
            />
          </div>
        )}
      </FlexboxColumn>
      <div className='mobile_bottom_padding'>
        <Line />
      </div>
    </FlexboxColumn>
  );
});

export default TwitterReviewDetailCard;
